/**
 * Translates markdown to HTML with specific formatting rules.
 *
 * These rules come directly from curriculum.  We should treat these as
 * temporary styles until we get a proper design for instructions.
 *
 * Changes from original, at https://github.com/aops-ba/vs-cs-curriculum/blob/main/src/html-translator.ts
 * - Removed Prism.js (language-specific highlighting)
 * - Removed JSDOM (unnecessary) in favor of creating a root DOM element
 * - Removed styles input (which live in MarkdownViewer.module.scss)
 **/
import {marked} from "marked";
import {html as beautifyHtml} from "js-beautify";

// Helper function to format HTML using js-beautify
function formatHTML(html: string): string {
	try {
		return beautifyHtml(html, {
			indent_size: 2,
			indent_char: " ",
			max_preserve_newlines: 1,
			preserve_newlines: true,
			indent_scripts: "keep",
			end_with_newline: true,
			wrap_line_length: 100,
			indent_inner_html: true,
			indent_empty_lines: false,
			unformatted: ["code", "pre", "em", "strong", "span"],
		});
	} catch (error) {
		console.error("Error formatting HTML:", error);
		return html; // Return original HTML if formatting fails
	}
}

// Helper function to process code blocks
function processCodeBlock(
	block: Element,
	rootElement: HTMLElement
): HTMLElement {
	const wrapper = document.createElement("div");
	wrapper.className = "code-block";

	const code = block.textContent || "";
	// SK: Prism does not work with webpack, so I'll just remove it for now
	// const highlightedCode = Prism.highlight(code, Prism.languages.java!, "java");

	// const highlightedBlock = document.createElement("code");
	// highlightedBlock.className = "language-java";
	// highlightedBlock.innerHTML = highlightedCode;

	const preBlock = document.createElement("pre");
	// preBlock.appendChild(highlightedBlock);
	preBlock.innerHTML = code;
	wrapper.appendChild(preBlock);

	return wrapper;
}

// Helper function to process tables
function processTable(
	table: HTMLTableElement,
	rootElement: HTMLElement
): HTMLTableElement {
	// Add the student-table class
	table.className = "student-table";

	// Ensure proper table structure
	if (!table.querySelector("thead")) {
		const firstRow = table.querySelector("tr");
		if (firstRow) {
			const thead = document.createElement("thead");
			firstRow.parentNode?.removeChild(firstRow);

			// Convert td to th in header row if needed
			const headerCells = firstRow.querySelectorAll("td");
			headerCells.forEach((cell) => {
				const th = document.createElement("th");
				th.innerHTML = cell.innerHTML;
				cell.parentNode?.replaceChild(th, cell);
			});

			thead.appendChild(firstRow);
			table.insertBefore(thead, table.firstChild);
		}
	}

	// Create tbody if it doesn't exist
	if (!table.querySelector("tbody")) {
		const tbody = document.createElement("tbody");
		Array.from(table.querySelectorAll("tr")).forEach((row) => {
			if (!row.closest("thead")) {
				tbody.appendChild(row);
			}
		});
		table.appendChild(tbody);
	}

	// Process all data rows to identify output cells
	const tbody = table.querySelector("tbody");
	if (tbody) {
		const rows = tbody.querySelectorAll("tr");
		rows.forEach((row) => {
			const cells = row.querySelectorAll("td");
			if (cells.length > 0) {
				const lastCell = cells[cells.length - 1];
				lastCell!.className = "output-cell";
			}
		});
	}

	return table;
}

// Helper function to process individual elements in default sections and put them in a container
function processDefaultElement(
	element: Element,
	rootElement: HTMLElement,
	container: Element
): void {
	if (element.tagName === "UL" || element.tagName === "P") {
		container.appendChild(element.cloneNode(true));
	} else if (element.tagName === "PRE") {
		const codeBlock = element.querySelector("code");
		if (codeBlock) {
			container.appendChild(processCodeBlock(codeBlock, rootElement));
		}
	} else if (element.tagName === "TABLE") {
		container.appendChild(
			processTable(element as HTMLTableElement, rootElement).cloneNode(true)
		);
	}
}

export function convertMarkdownToHTML(content: string): string {
	// First convert markdown to HTML
	const rawHTML = marked(content);

	// Create a temporary element to process the content
	const containerElement = document.getElementById("container");
	const testContainer = document.createElement("div");
	testContainer.style.position = "absolute";
	testContainer.style.top = "-100%";
	testContainer.style.left = "-100%";
	testContainer.style.width = "95%";
	testContainer.style.height = "95%";
	containerElement?.appendChild(testContainer);

	const testElement = document.createElement("div");
	testElement.innerHTML = rawHTML;
	testElement.className = "container";
	containerElement?.appendChild(testElement);

	// Process each section
	processIntroSections(testElement);
	processPreviewSections(testElement);
	processGivenSections(testElement);
	processTodoSections(testElement);
	processHintsSections(testElement);
	processDefaultSections(testElement);

	// Get the processed HTML
	const processedHTML = testElement.outerHTML || "";

	// Format the final HTML
	const formattedHTML = formatHTML(processedHTML);

	containerElement?.removeChild(testElement);

	return formattedHTML;
}

function processDefaultSections(rootElement: HTMLElement): void {
	// Get all h2 headers that aren't part of special sections
	const h2Headers = Array.from(rootElement.querySelectorAll("h2")).filter(
		(header) => {
			const headerText = header.textContent?.toLowerCase() || "";
			return (
				!headerText.includes("given") &&
				!headerText.includes("preview") &&
				!headerText.includes("to-do") &&
				!headerText.includes("hints")
			);
		}
	);

	// For each h2, throw a div around it and attach class names
	h2Headers.forEach((h2Header) => {
		h2Header.className = "center-text";
		const h2Container = document.createElement("div");
		h2Container.className = "single-item";

		// Collect all elements until next h2
		const content: Element[] = [];
		let currentElement = h2Header.nextElementSibling;
		while (currentElement && currentElement.tagName !== "H2") {
			content.push(currentElement);
			currentElement = currentElement.nextElementSibling;
		}

		// Go through the collected content, processing each element
		for (let i = 0; i < content.length; i++) {
			const element = content[i];

			// If we reach an h3, put a div around it and attach class names for css
			if (element?.tagName === "H3") {
				const h3Container = document.createElement("div");
				h3Container.className = "h3-container";

				// Add the h3 header into the container
				const h3Header = element.cloneNode(true) as HTMLElement;
				h3Header.className = "center-text";
				h3Container.appendChild(h3Header);

				// Create a container for all content under the h3
				const h3ContentContainer = document.createElement("div");
				h3ContentContainer.className = "generic-subsection-content";

				// Add all content until next h2 or h3 header into current h3 container
				let j = i + 1;
				while (
					j < content.length &&
					content?.[j]?.tagName !== "H3" &&
					content?.[j]?.tagName !== "H2"
				) {
					const h3ContentElement = content[j];

					processDefaultElement(
						h3ContentElement!,
						rootElement,
						h3ContentContainer
					);
					j++;
				}

				// Add the content container to the h3 container
				h3Container.appendChild(h3ContentContainer);

				// Add the h3 container to the h2's single-item div
				h2Container.appendChild(h3Container);
				// Skip the processed elements
				i = j - 1;

				// First section underneath h2 should be boxed even if not in an h3
			} else if (i === 0) {
				// Make a box for the content, treat it as same as an h3's container css-wise
				const firstElementsContainer = document.createElement("div");
				firstElementsContainer.className = "h3-container";

				// Process the content up until a header.  Start at i here since ith element is not an h3 header.
				let j = i;
				while (
					j < content.length &&
					content?.[j]?.tagName !== "H3" &&
					content?.[j]?.tagName !== "H2"
				) {
					const contentElement = content[j];

					processDefaultElement(
						contentElement!,
						rootElement,
						firstElementsContainer
					);
					j++;
				}

				// Add the first elements container to the h2's box
				h2Container.appendChild(firstElementsContainer);
				// Skip the processed elements
				i = j - 1;

				// Back to h2 content processing
			} else {
				processDefaultElement(element!, rootElement, h2Container);
			}
		}

		// Replace the old content
		h2Header.after(h2Container);
		content.forEach((element) => element.remove());
	});
}

// Updated to process preview sections
function processPreviewSections(rootElement: HTMLElement): void {
	const previewHeaders = Array.from(rootElement.querySelectorAll("h2")).filter(
		(header) => header.textContent?.toLowerCase().includes("preview")
	);

	previewHeaders.forEach((previewHeader, sectionIndex) => {
		previewHeader.className = "center-text";

		// Create preview container structure
		const toggleInput = document.createElement("input");
		toggleInput.type = "checkbox";
		toggleInput.id = `preview-toggle-${sectionIndex}`;
		toggleInput.className = "preview-toggle";

		const toggleLabel = document.createElement("label");
		toggleLabel.setAttribute("for", toggleInput.id);
		toggleLabel.className = "preview-section";

		// Create preview content container
		const previewContent = document.createElement("div");
		previewContent.className = "preview-content";

		// Add header to preview content
		const contentHeader = document.createElement("h2");
		contentHeader.className = "center-text";
		contentHeader.textContent = "Preview";
		previewContent.appendChild(contentHeader);

		// Create blue box for media content
		const blueBox = document.createElement("div");
		blueBox.className = "blue-box center-text";

		// Find and process media elements
		let currentElement = previewHeader.nextElementSibling;
		const processedElements = new Set<Element>();

		while (currentElement && currentElement.tagName !== "H2") {
			if (
				currentElement.tagName === "P" &&
				!processedElements.has(currentElement)
			) {
				const mediaElement = currentElement.querySelector("img, video");
				if (mediaElement) {
					// Clone the media element to preserve its attributes
					const newMediaElement = mediaElement.cloneNode(true) as HTMLElement;
					blueBox.appendChild(newMediaElement);
					processedElements.add(currentElement);
				}
			}
			currentElement = currentElement.nextElementSibling;
		}

		previewContent.appendChild(blueBox);

		// Create placeholder for collapsed state
		const previewPlaceholder = document.createElement("div");
		previewPlaceholder.className = "preview-placeholder";
		previewPlaceholder.textContent = "Show Preview";

		// Assemble the preview section
		toggleLabel.appendChild(previewContent);
		toggleLabel.appendChild(previewPlaceholder);

		// Insert the new preview structure
		previewHeader.before(toggleInput);
		previewHeader.replaceWith(toggleLabel);

		// Clean up processed elements
		processedElements.forEach((element) => {
			element.remove();
		});
	});
}

function processGivenSections(rootElement: HTMLElement): void {
	const givenHeaders = Array.from(rootElement.querySelectorAll("h2")).filter(
		(header) => header.textContent?.toLowerCase().includes("given")
	);

	givenHeaders.forEach((givenHeader) => {
		givenHeader.className = "center-text";

		// Create single-item container
		const singleItem = document.createElement("div");
		singleItem.className = "single-item";

		let currentElement = givenHeader.nextElementSibling;
		const processedElements = new Set<Element>();

		while (currentElement && currentElement.tagName !== "H2") {
			if (
				currentElement.tagName === "UL" &&
				!processedElements.has(currentElement)
			) {
				// Clone the UL element to preserve its structure
				const newUl = currentElement.cloneNode(true) as HTMLElement;
				singleItem.appendChild(newUl);
				processedElements.add(currentElement);
			}
			currentElement = currentElement.nextElementSibling;
		}

		// Insert the single-item container after the header
		givenHeader.after(singleItem);

		// Clean up processed elements
		processedElements.forEach((element) => {
			element.remove();
		});
	});
}

function processIntroSections(rootElement: HTMLElement): void {
	const titleHeaders = rootElement.querySelectorAll("h1");
	titleHeaders.forEach((titleHeader, index) => {
		titleHeader.className = "center-text";
		const introText = titleHeader.nextElementSibling;

		if (introText) {
			const introBox = document.createElement("div");
			introBox.className = "intro-box";

			// Add two line breaks before non-first intro sections
			if (index > 0) {
				const breakElements = document.createElement("div");
				breakElements.innerHTML = "<br><br>";
				titleHeader.parentNode?.insertBefore(breakElements, titleHeader);
			}

			introBox.innerHTML = introText.innerHTML;
			introText.replaceWith(introBox);
		}
	});
}

function processTodoSections(rootElement: HTMLElement): void {
	const todoHeaders = Array.from(rootElement.querySelectorAll("h2")).filter(
		(header) => header.textContent?.includes("To-Do")
	);

	todoHeaders.forEach((todoHeader, sectionIndex) => {
		todoHeader.className = "center-text";
		// Create todo container
		const todoContainer = document.createElement("div");
		todoContainer.className = "todo-container";

		let currentElement = todoHeader.nextElementSibling;
		const processedElements = new Set<Element>();

		while (currentElement && currentElement.tagName !== "H2") {
			const nextElement = currentElement.nextElementSibling;

			if (
				currentElement.tagName === "P" &&
				!processedElements.has(currentElement)
			) {
				// This is a skill category header (GREEN/YELLOW/RED)
				const text = currentElement.textContent || "";
				const todoItem = document.createElement("div");
				todoItem.className = "todo-item";
				const todoTitle = document.createElement("div");
				todoTitle.className = "todo-title";

				// Convert status indicators to emojis
				let levelEmoji = "";
				if (text.includes("(GREEN)")) {
					levelEmoji = "🟢";
				} else if (text.includes("(YELLOW)")) {
					levelEmoji = "🟡";
				} else if (text.includes("(RED)")) {
					levelEmoji = "🔴";
				}

				// Extract the actual title (remove all possible color indicators)
				const titleText = text.replace(/\((GREEN|YELLOW|RED)\)\s*/, "");
				todoTitle.textContent = `${levelEmoji} ${titleText}`;
				todoItem.appendChild(todoTitle);

				// Find the following ul element
				const ul = nextElement?.tagName === "UL" ? nextElement : null;
				if (ul) {
					const newUl = document.createElement("ul");

					// Process each li element
					Array.from(ul.children).forEach((li, index) => {
						const newLi = document.createElement("li");

						// Create checkbox
						const checkbox = document.createElement("input");
						checkbox.type = "checkbox";
						// Use emoji type and section index for unique IDs
						checkbox.id = `todo-${sectionIndex}-${
							levelEmoji === "🟢" ? "0" : levelEmoji === "🟡" ? "1" : "2"
						}-${index + 1}`;
						checkbox.className = "todo-checkbox";

						// Create label
						const label = document.createElement("label");
						label.setAttribute("for", checkbox.id);
						//const p = document.createElement("p");
						label.innerHTML = li.innerHTML;
						//label.appendChild(p);

						newLi.appendChild(checkbox);
						newLi.appendChild(label);
						newUl.appendChild(newLi);
					});

					todoItem.appendChild(newUl);
					processedElements.add(currentElement);
					processedElements.add(ul);
				}
				todoContainer.appendChild(todoItem);
			}
			currentElement = nextElement;
		}

		// Clean up processed elements
		processedElements.forEach((element) => {
			element.remove();
		});

		// Insert the todo container after the header
		todoHeader.after(todoContainer);
	});
}

function processHintsSections(rootElement: HTMLElement): void {
	const hintsHeaders = Array.from(rootElement.querySelectorAll("h2")).filter(
		(header) => header.textContent?.toLowerCase().includes("hints")
	);

	hintsHeaders.forEach((hintsHeader, sectionIndex) => {
		hintsHeader.className = "center-text";

		// Create hints container
		const hintsContainer = document.createElement("div");
		hintsContainer.className = "hints-container";

		let currentElement = hintsHeader.nextElementSibling;
		const processedElements = new Set<Element>();
		let categoryCount = 0;

		while (currentElement && currentElement.tagName !== "H2") {
			if (
				currentElement.tagName === "H3" &&
				!processedElements.has(currentElement)
			) {
				categoryCount++;
				const hintCategory = document.createElement("div");
				hintCategory.className = "hint-category";

				// Create category checkbox with unique section identifier
				const categoryCheckbox = document.createElement("input");
				categoryCheckbox.type = "checkbox";
				const categoryId = `var(--project-prefix)category${sectionIndex}-${categoryCount}`;
				categoryCheckbox.id = categoryId;
				categoryCheckbox.className = "category-checkbox";

				// Create category label
				const categoryLabel = document.createElement("label");
				categoryLabel.setAttribute("for", categoryId);
				categoryLabel.className = "category-title";
				categoryLabel.textContent = currentElement.textContent || "";

				// Create category content
				const categoryContent = document.createElement("div");
				categoryContent.className = "category-content";

				// Process hints
				let nextElement = currentElement.nextElementSibling;
				let hintIndex = 1;

				while (nextElement && nextElement.tagName === "UL") {
					Array.from(nextElement.children).forEach((li) => {
						const hint = document.createElement("div");
						hint.className = "hint";

						// Create hint checkbox with unique section identifier
						const hintCheckbox = document.createElement("input");
						hintCheckbox.type = "checkbox";
						const hintId = `var(--project-prefix)hint${sectionIndex}-${categoryCount}-${hintIndex}`;
						hintCheckbox.id = hintId;
						hintCheckbox.className = "hint-checkbox";

						// Create hint title
						const hintTitle = document.createElement("label");
						hintTitle.setAttribute("for", hintId);
						hintTitle.className = "hint-title";
						hintTitle.textContent = "Reveal";

						// Create hint content
						const hintContent = document.createElement("div");
						hintContent.className = "hint-content";
						const p = document.createElement("p");
						p.innerHTML = li.innerHTML;
						hintContent.appendChild(p);

						// Assemble hint
						hint.appendChild(hintCheckbox);
						hint.appendChild(hintTitle);
						hint.appendChild(hintContent);
						categoryContent.appendChild(hint);

						hintIndex++;
					});

					processedElements.add(nextElement);
					nextElement = nextElement.nextElementSibling;
				}

				// Assemble category
				hintCategory.appendChild(categoryCheckbox);
				hintCategory.appendChild(categoryLabel);
				hintCategory.appendChild(categoryContent);
				hintsContainer.appendChild(hintCategory);

				processedElements.add(currentElement);
			}

			currentElement = currentElement.nextElementSibling;
		}

		// Clean up processed elements
		processedElements.forEach((element) => {
			element.remove();
		});

		// Insert hints container after header
		hintsHeader.after(hintsContainer);
	});
}
